import noop from 'lodash/noop';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useStaticCallback from 'hooks/useStaticCallback';
import { useUnitId } from 'state/unit';
import { useUnitPlaybackManager } from 'state/unitPlaybackManager';
import { DrawableAudioPlayer } from 'utils/audio/types';
import { PLAYER_DEFAULT_VOLUME } from 'utils/constants';
import { PlaybackContextType } from './types';
import usePlaybackControls from './usePlaybackControls';
import usePlaybackEventTracking from './usePlaybackEventTracking';
import usePlaybackState from './usePlaybackState';
import useWaveformPreview from './useWaveformPreview';

export interface PlaybackProviderProps {
  animatePreview?: boolean;
  autoplay?: boolean;
  children?: React.ReactNode;
  defaultVolume?: number;
  loop?: boolean;
  onEnded?: () => void;
  onInteraction?: () => void;
  onPause?: () => void;
  onPlaying?: () => void;
  player?: DrawableAudioPlayer;
  previewPlayer?: DrawableAudioPlayer;
}

const PlaybackContext = createContext<PlaybackContextType | undefined>(
  undefined,
);

export const PlaybackProvider: React.FC<PlaybackProviderProps> = ({
  animatePreview,
  autoplay,
  defaultVolume = PLAYER_DEFAULT_VOLUME,
  loop,
  onEnded = noop,
  onInteraction,
  onPause = noop,
  onPlaying = noop,
  player,
  previewPlayer,
  children,
}) => {
  const [volume, setVolume] = useState(animatePreview ? 0 : defaultVolume);
  const { unitId } = useUnitId();

  const { isPlaying: isPreviewActive } = useWaveformPreview({
    enabled: !!animatePreview,
    onPause: useCallback(() => {
      setVolume(defaultVolume);
    }, [defaultVolume]),
    waveformPlayer: previewPlayer,
  });

  const activePlayer = isPreviewActive ? previewPlayer : player;

  const { pause, play, playingUnitId } = useUnitPlaybackManager(
    unitId,
    autoplay,
  );

  const { playbackState } = usePlaybackState({ loop, player });
  const { playbackState: previewPlaybackState } = usePlaybackState({
    loop: true,
    player: previewPlayer,
  });

  const [ready, setReady] = useState(!!player?.isReady);

  const playbackControls = usePlaybackControls({
    onInteraction,
    playbackState,
    player,
    loadedUnitId: unitId,
    onPause: useCallback(() => pause(), [pause]),
    onPlay: useCallback(() => play(), [play]),
  });

  usePlaybackEventTracking({
    // don't use activePlayer - only want to track events when audio is actually playing
    player,
    playingUnitId,
    volume,
    loadedUnitId: unitId,
  });

  const handleEnded = useCallback(() => {
    if (loop) {
      player?.seek(0);
      player?.play();
    } else {
      // when audio ends, the unitPlayer must be informed so that the playingUnitId
      // is cleared
      pause();
    }
    onEnded();
  }, [loop, onEnded, pause, player]);

  // the effect hook below should run only when the plaer changes, so all function
  // dependencies must be static
  const staticOnPause = useStaticCallback(onPause);
  const staticOnPlaying = useStaticCallback(onPlaying);
  const staticHandleEnded = useStaticCallback(handleEnded);

  // effect to handle `player` changing
  useEffect(() => {
    if (isPreviewActive) {
      return;
    }

    let active = true;

    // handleWaiting and handleCanPlay are defined within this hook because they
    // need to check `active` before setting state to avoid updating state if the
    // player is orphaned or the component is unmounted
    const handleWaiting = () => {
      if (active) {
        setReady(false);
      }
    };
    const handleCanPlay = () => {
      if (active) {
        setReady(true);
      }
    };

    async function initializePlayer() {
      if (player) {
        try {
          player.on('ended', staticHandleEnded);
          player.on('pause', staticOnPause);
          player.on('playing', staticOnPlaying);
          player.on('waiting', handleWaiting);
          player.on('canplay', handleCanPlay);
        } catch {
          // TODO define error behavior
        }
      }
    }

    initializePlayer();

    return () => {
      active = false;
      if (player) {
        // if the player isn't paused, pause it before loading the new player.
        // special handling is needed for the onPause prop since calling
        // player.pause() and then immediately removing the onPause event handler
        // will result in the callback not getting executed for this final pause
        // operation
        if (!player.paused) {
          const handleFinalPause = () => {
            player.off('pause', handleFinalPause);
            player.off('pause', staticOnPause);
          };
          player.on('pause', handleFinalPause);

          player.pause();
        } else {
          player.off('pause', staticOnPause);
        }

        player.off('ended', staticHandleEnded);
        player.off('playing', staticOnPlaying);
        player.off('waiting', handleWaiting);
        player.off('canplay', handleCanPlay);
      }
      setReady(false);
    };
  }, [
    player,
    staticHandleEnded,
    staticOnPause,
    staticOnPlaying,
    isPreviewActive,
  ]);

  // syncs player with unit playback state
  // in cases where there are multiple players, each player will have its own
  // unitId and have access to the currently playing unit id.  If the currently
  // playing unit id is not ours, pause our audio.
  //
  // this hook can lead to audio continuing to play after the component is unmounted.
  // if making changes to this hook, check that functionality still works by playing
  // audio and calling `reactRootElement.remove()` to ensure audio is paused
  // see SPAR-17206
  useEffect(() => {
    if (playingUnitId !== unitId) {
      // NB: don't call handlePause - that function will replace the currently
      // playing unit id with undefined, effectively pausing another player's audio
      player?.pause();
    }

    // if our unit is playing, start the player
    if (playingUnitId === unitId) {
      player?.play();
    }
  }, [player, playingUnitId, unitId]);

  // effect to handle volume changes
  // historyically, the PlaybackContext often gets destroyed by adding a key
  // prop so that when something changes (e.g. audio source), the context gets
  // re-created at its base state.  volume, however, is supposed to persist across
  // all audio sources and so is stored somewhere else (see useUnitPlayer).
  // this hook applies the volume to the player.
  useEffect(() => {
    if (!isPreviewActive && player) {
      player.volume = volume;
    }
  }, [isPreviewActive, player, volume]);

  const contextValue = useMemo(
    () => ({
      isPreviewActive,
      playbackState: isPreviewActive ? previewPlaybackState : playbackState,
      ready: isPreviewActive ? true : ready,
      setVolume,
      volume,
      animatePreview: !!animatePreview,
      onTogglePlay: playbackControls.togglePlay,
      pause: playbackControls.pause,
      play: playbackControls.play,
      player: activePlayer,
    }),
    [
      activePlayer,
      animatePreview,
      isPreviewActive,
      playbackControls.pause,
      playbackControls.play,
      playbackControls.togglePlay,
      playbackState,
      previewPlaybackState,
      ready,
      volume,
    ],
  );

  return (
    <PlaybackContext.Provider value={contextValue}>
      {children}
    </PlaybackContext.Provider>
  );
};

export function usePlayback() {
  const context = useContext(PlaybackContext);

  if (!context) {
    throw new Error('usePlayback must be used within PlaybackProvider');
  }

  return context;
}
